/**
 * WelcomeStep — the cinematic opener. Shield mark, three promise cards
 * lined up like a regulator-grade certificate, and a subtle ambient layer
 * (dotted grid + drifting waves) so the page feels animated without being
 * busy. The promises are deliberately short — what changes for the
 * inspector tomorrow morning.
 */
import React from 'react';
import { Sparkles, ShieldCheck, Anchor, Compass } from 'lucide-react';
import StepShell from './StepShell';
import { BrandMark } from '../../../components/brand/BrandLogo';
import StatusPill from '../../../components/brand/StatusPill';
import { StepId } from '../types';

interface Props {
  onNext: () => void;
  onSkip: () => void;
  inspectorName?: string;
}

export default function WelcomeStep({ onNext, onSkip, inspectorName }: Props) {
  const greeting = inspectorName ? `Welcome aboard, ${inspectorName}.` : 'Welcome aboard, Inspector.';
  return (
    <StepShell
      step={'welcome' as StepId}
      onNext={onNext}
      onSkip={onSkip}
      nextLabel="Begin the tour"
    >
      {/* Hero band */}
      <div className="relative overflow-hidden rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-8 md:p-12"
           style={{ boxShadow: '0 0 60px rgba(6,182,212,0.15)' }}>
        {/* drifting wave pattern */}
        <svg
          className="absolute inset-x-0 bottom-0 w-full text-cyan-500/20 pointer-events-none"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,60 C200,90 400,30 600,60 C800,90 1000,30 1200,60 L1200,120 L0,120 Z"
            fill="currentColor"
          />
        </svg>

        <div className="relative flex flex-col md:flex-row items-start md:items-center gap-8">
          <div className="shrink-0">
            <BrandMark size={84} tone="dark" />
          </div>
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-cyan-300" />
              <span className="text-[11px] uppercase tracking-widest font-bold text-cyan-300">
                Maritime Intelligence · Inspection Cockpit
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
              {greeting}
            </h2>
            <p className="mt-3 text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed">
              VarunAIq is your AI-augmented vetting workbench — live AIS,
              regulator-format reports, every IMO standard a click away.
              The next five minutes will show you what's behind it.
            </p>

            {/* status-pill demo strip — proves the visual vocabulary up front */}
            <div className="mt-6 flex flex-wrap gap-2">
              <StatusPill variant="compliant"   size="sm" />
              <StatusPill variant="observation" size="sm" />
              <StatusPill variant="non_compliant" size="sm" />
              <StatusPill variant="reference"  size="sm" />
            </div>
          </div>
        </div>
      </div>

      {/* Three promises */}
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <PromiseCard
          Icon={Anchor}
          title="One workbench, every regime"
          body="CDI 10.5, RISQ 3.2, TMSA 3 — same flow, same shortcuts. Switch regimes without switching tools."
        />
        <PromiseCard
          Icon={ShieldCheck}
          title="Reports the regulator already knows"
          body="Pre-formatted findings, linked IMO refs, SOLAS citations rendered in the layout your auditor expects."
        />
        <PromiseCard
          Icon={Compass}
          title="Intelligence before you board"
          body="Live AIS track, last-port history, sanctions exposure, prior-visit findings — all on the prep page."
        />
      </div>

      <p className="mt-8 text-center text-xs text-slate-500">
        This tour takes about 5 minutes. You can revisit it anytime from the Help menu.
      </p>
    </StepShell>
  );
}

function PromiseCard({
  Icon,
  title,
  body,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-5 hover:border-cyan-500/40 hover:bg-slate-900/80 transition-all duration-200">
      <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-3">
        <Icon className="w-5 h-5 text-cyan-300" />
      </div>
      <h3 className="text-base font-bold text-white mb-1">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
}
